import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  LinearProgress,
  Button,
  Chip,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DialogAnimate from "src/components/Animate/DialogAnimate";
import { useDispatch, useSelector } from "src/redux/store";
import { cancelCashDrawer, dropTx, upsertTx } from "src/redux/slices/cashdrawer";

// Modal de espera mientras el cajón físico procesa la transacción.
//
// Estados que puede tener `tx.status`:
//   pending    — creada, aún no despachada al Worker.
//   dispatched — Worker recibió, escribió el fichero, esperando hardware.
//   completed  — cliente pagó el importe exacto.
//   partial    — atasco / pago parcial (< pedido).
//   cancelled  — user canceló desde el TPV o desde el hardware.
//   jam        — atasco sin cobro parcial usable.
//   timeout    — hardware no respondió antes del deadline (backend sweeper).
//   error      — XML corrupto u otro fallo.
//
// El dialog cierra automáticamente al detectar estado terminal
// (completed | partial | cancelled | jam | timeout | error) via
// snapshot del slice (que se rellena por hub SignalR).
export default function CashDrawerWaitDialog({ transactionId, amountCents, onResolved, onClose }) {
  const dispatch = useDispatch();
  const [seconds, setSeconds] = useState(0);
  const [cancelling, setCancelling] = useState(false);
  const tx = useSelector((s) => s.cashdrawer.pendingTx[transactionId]);

  useEffect(() => {
    if (!transactionId) return undefined;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [transactionId]);

  // Detectar transición a estado terminal → notificar al padre.
  useEffect(() => {
    if (!tx) return;
    if (isTerminal(tx.status)) {
      onResolved?.(tx);
      // Limpiar tras el callback para que subscribers puedan leer
      // el snapshot final una última vez.
      setTimeout(() => dispatch(dropTx(transactionId)), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tx?.status, transactionId]);

  const handleCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    // Optimistic: seteamos el tx local a "cancelling" para feedback
    // visual inmediato. El backend responderá con snapshot cancelled
    // que dispara el flujo terminal por el useEffect de arriba.
    try {
      dispatch(upsertTx({
        ...(tx || {}),
        id: transactionId,
        status: "cancelling",
      }));
      await dispatch(cancelCashDrawer(transactionId));
      // Fallback: si el hub no notifica en 3s, forzamos el cierre
      // marcando terminal local. Evita que el modal quede colgado
      // si el broadcast se pierde (aunque el backend ya persistió
      // el cancel).
      setTimeout(() => {
        dispatch(upsertTx({
          id: transactionId,
          status: "cancelled",
          errorMessage: "Cancelado desde el TPV",
          amountRequestedCents: amountCents,
        }));
      }, 3000);
    } catch {
      // Aunque falle el POST, cerramos localmente — el user ya
      // pulsó cancel, no puede quedarse atrapado.
      dispatch(upsertTx({
        id: transactionId,
        status: "cancelled",
        errorMessage: "Cancelado (backend no confirmó — revisa la caja manualmente)",
        amountRequestedCents: amountCents,
      }));
    } finally {
      setCancelling(false);
    }
  };

  const euros = useMemo(() => (amountCents / 100).toFixed(2), [amountCents]);
  const statusLabel = statusToLabel(tx?.status);
  // Progresos en vivo — mostramos SIEMPRE los 3 campos aunque estén a
  // 0, así el camarero ve la casilla desde el segundo 1 y sabe qué se
  // rellenará. Con hardware Sanjosat solo se rellena al final del cobro;
  // con imConnector (cuando lo integremos) el Worker polleará el estado
  // cada 1s y hará POST /cashdrawer/progress → snapshots parciales.
  const receivedCents = tx?.amountReceivedCents ?? 0;
  const changeCents = tx?.amountChangeCents ?? 0;
  const netCharged = Math.max(0, receivedCents - changeCents);
  const remaining = Math.max(0, amountCents - netCharged);
  const receivedEuros = (receivedCents / 100).toFixed(2);
  const changeEuros = (changeCents / 100).toFixed(2);
  const remainingEuros = (remaining / 100).toFixed(2);
  const hasProgress = receivedCents > 0 || changeCents > 0;

  return (
    <DialogAnimate open={!!transactionId} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: "center", pb: 0 }}>
        <Typography variant="overline" sx={{ color: "text.secondary" }}>
          Esperando al cajón automático
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
          <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: -1, lineHeight: 1 }}>
            {euros}€
          </Typography>

          {/* Chip de estado — cambia según el snapshot llegue por hub */}
          {statusLabel && (
            <Chip
              size="small"
              label={statusLabel.text}
              color={statusLabel.color}
              variant={statusLabel.variant}
              sx={{ fontWeight: 700 }}
            />
          )}

          <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={64} thickness={4} color={cancelling ? "warning" : "primary"} />
            <Typography sx={{ position: "absolute", fontWeight: 700, fontSize: 14 }}>
              {seconds}s
            </Typography>
          </Box>

          {/* Progresos en vivo — siempre visibles. Recibido y Cambio
              en gris hasta que el hardware reporte, luego resaltados.
              "Faltan" en ámbar cuando aún no cubrimos el pedido, verde
              cuando ya está cubierto (a la espera de cierre del cliente). */}
          <Box sx={{
            width: "100%",
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: "background.neutral",
            border: 1,
            borderColor: "divider",
          }}>
            <Stack spacing={0.75}>
              <Row label="Importe pedido" value={`${euros}€`} />
              <Row
                label="Metió el cliente"
                value={hasProgress ? `${receivedEuros}€` : "—"}
                muted={!hasProgress}
                highlight={hasProgress}
              />
              <Row
                label="Cambio devuelto"
                value={changeCents > 0 ? `${changeEuros}€` : "—"}
                muted={changeCents === 0}
              />
              <Row
                label={remaining > 0 ? "Faltan" : "Cubierto"}
                value={remaining > 0 ? `${remainingEuros}€` : "✓"}
                color={remaining > 0 ? "warning.dark" : "success.dark"}
                highlight
              />
            </Stack>
          </Box>
          {!hasProgress && (
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              (los importes se actualizarán a medida que el cliente introduce el dinero)
            </Typography>
          )}

          <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", maxWidth: 320 }}>
            {cancelling
              ? "Cancelando la operación en el cajón…"
              : "El cajón está procesando el cobro. Cuando el cliente termine, el pago se aplicará al pedido automáticamente."}
          </Typography>

          {/* Mensaje del hardware si viene (atasco, warning, etc) */}
          {tx?.errorMessage && (
            <Typography
              variant="caption"
              sx={{
                color: "warning.dark",
                textAlign: "center",
                bgcolor: "warning.lighter",
                px: 1.5, py: 0.75, borderRadius: 1,
              }}
            >
              {tx.errorMessage}
            </Typography>
          )}

          <LinearProgress
            sx={{ width: "100%", borderRadius: 999 }}
            color={cancelling ? "warning" : "primary"}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
        <Button color="inherit" onClick={handleCancel} disabled={cancelling}>
          {cancelling ? "Cancelando…" : "Cancelar"}
        </Button>
      </DialogActions>
    </DialogAnimate>
  );
}

function Row({ label, value, highlight, muted, color }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color={muted ? "text.disabled" : "text.secondary"}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: highlight ? 800 : 600,
          color: color || (muted ? "text.disabled" : "text.primary"),
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function isTerminal(status) {
  return status === "completed" || status === "partial" || status === "cancelled" ||
         status === "jam"       || status === "timeout" || status === "error";
}

// Etiquetas humanas por estado — el chip cambia de color según severity.
function statusToLabel(status) {
  switch (status) {
    case "pending":    return { text: "En cola", color: "default", variant: "outlined" };
    case "dispatched": return { text: "Enviado al cajón", color: "info", variant: "outlined" };
    case "cancelling": return { text: "Cancelando…", color: "warning", variant: "outlined" };
    case "cancelled":  return { text: "Cancelado", color: "warning", variant: "filled" };
    case "partial":    return { text: "Parcial (atasco)", color: "warning", variant: "filled" };
    case "jam":        return { text: "ATASCO", color: "error", variant: "filled" };
    case "timeout":    return { text: "Sin respuesta", color: "error", variant: "filled" };
    case "error":      return { text: "Error", color: "error", variant: "filled" };
    case "completed":  return { text: "Cobrado ✓", color: "success", variant: "filled" };
    default:           return null;
  }
}
